import { useCallback } from "react";
import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  queryHelpers,
  styled,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import {
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from "@leagueplatform/analytics";
import { useIntl } from "@leagueplatform/locales";
import { toastActions, TOAST_STATUS } from "@leagueplatform/toast-messages";
import {
  ANALYTICS_SCREEN_NAMES,
  DETAIL,
  PRODUCT_AREA_NAMES,
  SECONDARY_CONTENT_MAX_WIDTH,
  SUB_PRODUCT_AREA_NAMES,
} from "common/constants";
import { OnboardingFlowContainer } from "components/onboarding-flow-container/onboarding-flow-container.component";
import { useSetGoPaperless } from "hooks/use-set-go-paperless/use-set-go-paperless.hook";
import PaperlessImage from "assets/paperless.svg";

const FullScreenContainer = styled(StackLayout, {
  width: "100vw",
  height: "100dvh",
  position: "fixed",
  top: "0",
  left: "0",
  zIndex: "$sticky",
  overflowY: "auto",
});

type Props = {
  onContinue: () => void;
};

export const GoPaperlessPage = ({ onContinue }: Props) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));
  const { mutateAsync: setGoPaperless, isLoading } = useSetGoPaperless();

  usePageViewAnalytics({
    product_area: PRODUCT_AREA_NAMES.FOUNDATION,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
    screen_name: ANALYTICS_SCREEN_NAMES.GO_PAPERLESS,
  });

  const trackButtonClick = useCallback((detail: string) => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA_NAMES.FOUNDATION,
      sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
      screen_name: ANALYTICS_SCREEN_NAMES.GO_PAPERLESS,
      detail,
    });
  }, []);

  const handleOptIn = useCallback(async () => {
    trackButtonClick(DETAIL.OPT_IN_PAPERLESS);

    try {
      await setGoPaperless(true);
      onContinue();
    } catch {
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        text: formatMessage({ id: "STR_ERROR_STATE_BODY_API" }),
      });
    }
  }, [trackButtonClick, setGoPaperless, onContinue, formatMessage]);

  const handleNotNow = useCallback(() => {
    trackButtonClick(DETAIL.NOT_NOW);
    onContinue();
  }, [trackButtonClick, onContinue]);

  return (
    <FullScreenContainer
      css={{
        background: "$surfaceBackgroundPrimary",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <OnboardingFlowContainer
        isMobile={isMobile}
        css={{
          maxWidth: SECONDARY_CONTENT_MAX_WIDTH,
          width: "100%",
          margin: "auto",
        }}
      >
        <StackLayout
          horizontalAlignment="center"
          verticalAlignment="center"
          css={{ maxWidth: "468px", margin: "auto" }}
        >
          <img
            src={PaperlessImage}
            height="176"
            width="176"
            alt={formatMessage({ id: "STR_GO_PAPERLESS_TITLE" })}
          />
          <StackItem
            horizontalAlignment="center"
            css={{
              marginBlock: "$twoAndHalf",
              textAlign: "center",
              gap: "$half",
            }}
          >
            <HeadingText level="3" size="lg">
              {formatMessage({ id: "STR_GO_PAPERLESS_TITLE" })}
            </HeadingText>
            <ParagraphText
              css={{
                typography: "$bodyOne",
                color: "$onSurfaceTextSubdued",
              }}
            >
              {formatMessage({ id: "STR_GO_PAPERLESS_BODY" })}
            </ParagraphText>
          </StackItem>
          <StackItem css={{ gap: "$oneAndHalf", width: "100%" }}>
            <Button
              width="fillContainer"
              loading={isLoading}
              onClick={handleOptIn}
            >
              {formatMessage({ id: "STR_OPT_IN_PAPERLESS" })}
            </Button>
            <Button
              width="fillContainer"
              priority="tertiary"
              disabled={isLoading}
              onClick={handleNotNow}
            >
              {formatMessage({ id: "STR_NOT_NOW" })}
            </Button>
          </StackItem>
        </StackLayout>
      </OnboardingFlowContainer>
    </FullScreenContainer>
  );
};
